import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { WarningOctagon } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RecordLossButton = ({ productId, productType, productName, currentStock, onDone }) => {
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState(1);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) {
      toast.error('Une justification est requise');
      return;
    }
    if (qty <= 0 || qty > currentStock) {
      toast.error(`Quantité invalide (1..${currentStock})`);
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/losses`,
        { product_id: productId, product_type: productType, quantity: parseInt(qty), reason: reason.trim() },
        { withCredentials: true }
      );
      toast.success(`${qty} unité(s) mise(s) en perte`);
      setOpen(false);
      setQty(1);
      setReason('');
      if (onDone) onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  if (currentStock <= 0) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`record-loss-btn-${productId}`}
        className="inline-flex items-center justify-center p-2 text-amber-600 hover:bg-amber-50 rounded-md"
        title="Mettre en perte (casse, péremption, etc.)"
      >
        <WarningOctagon size={18} />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="loss-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mettre en perte — {productName}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="text-sm text-slate-600">
              Stock actuel : <strong>{currentStock}</strong>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Quantité à mettre en perte *</label>
              <Input
                type="number"
                min="1"
                max={currentStock}
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                data-testid="loss-quantity-input"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Justification *</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="loss-reason-input"
                rows={3}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                placeholder="Casse, péremption, vol, retour fournisseur, défaut de fabrication..."
                required
              />
              <p className="text-xs text-slate-500 mt-1">Cette justification sera conservée pour les contrôles comptables.</p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={saving} data-testid="submit-loss-btn" className="bg-amber-600 hover:bg-amber-500 text-white">
                {saving ? 'Enregistrement...' : 'Confirmer la perte'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RecordLossButton;
